import type { AtcVersion, CreateAtcVersion } from '@capsio/contracts';
import { useState, type ReactNode } from 'react';
import { api, messageOf } from '../api-client.js';
import { BusyButton } from '../components/Field.js';

/**
 * 四段填写（C-096）。
 *
 * **分三屏填，一次提交整份八字段。**
 *
 * 为什么不做成「只补一个字段」：版本行是整份快照。允许只补 `how`，
 * 服务端就得先读当前版再合并——那一步出错，新版会带上旧的其他字段而无人察觉。
 * 所以未改的字段**由这里从当前版预填，并显式标「沿用上一版」**：
 * 让人看见自己在提交什么，而不是让服务端替他猜。
 *
 * **这个组件不置待办为完成。** 填完表不等于做对了；
 * 由填表的人自己宣布通过，那道门就形同虚设。
 */

const SCREENS = [
  { key: 'what-how', label: '① WHAT / HOW', blocks: '建设中' },
  { key: 'action', label: '② ACTION / 触发', blocks: '评审中' },
  { key: 'test', label: '③ TEST / 成熟度', blocks: '定义认证' },
] as const;

type ScreenKey = (typeof SCREENS)[number]['key'];

/** 「沿用上一版」标记。**提在模块顶层**——组件不能在渲染中创建，否则每次渲染都是新类型。 */
function Reused({ when }: { when: boolean }): ReactNode {
  return when ? <span className="chip plain">沿用上一版</span> : null;
}

/** jsonb 字段用文本框收，提交前解析——解析不了就不让提交，不静默当成空对象。 */
function useJsonField(initial: unknown): {
  text: string;
  setText: (v: string) => void;
  error: string | null;
  value: Record<string, unknown> | null;
} {
  const [text, setText] = useState(() => JSON.stringify(initial ?? {}, null, 2));
  let value: Record<string, unknown> | null = null;
  let error: string | null = null;
  try {
    const parsed: unknown = JSON.parse(text === '' ? '{}' : text);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      error = '要是一个 JSON 对象（大括号包起来）';
    } else {
      value = parsed as Record<string, unknown>;
    }
  } catch {
    error = 'JSON 格式不对';
  }
  return { text, setText, error, value };
}

export function AtcVersionForm({
  code,
  current,
  onSubmitted,
}: {
  code: string;
  /** 当前生效的版本，用来预填。null = 这张卡还没有任何版本。 */
  current: AtcVersion | null;
  onSubmitted: () => void;
}): ReactNode {
  const [screen, setScreen] = useState<ScreenKey>('what-how');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [what, setWhat] = useState(current?.what ?? '');
  const [how, setHow] = useState(current?.how ?? '');
  const [triggerType, setTriggerType] = useState(current?.triggerType ?? 'undetermined');
  const [hotlGate, setHotlGate] = useState(current?.hotlGate ?? false);
  const [determinism, setDeterminism] = useState(current?.determinism ?? 'deterministic');
  const actionSpec = useJsonField(current?.actionSpec);
  const testSpec = useJsonField(current?.testSpec);
  const triggerSpec = useJsonField(current?.triggerSpec);

  /** 哪些字段与当前版一致——用来在界面上标「沿用上一版」。 */
  const same = {
    what: current !== null && what === current.what,
    how: current !== null && how === current.how,
    triggerType: current !== null && triggerType === current.triggerType,
    determinism: current !== null && determinism === current.determinism,
    hotlGate: current !== null && hotlGate === current.hotlGate,
  };

  const jsonError = actionSpec.error ?? testSpec.error ?? triggerSpec.error;

  async function submit(): Promise<void> {
    if (jsonError !== null) {
      setError(`还有 JSON 没填对：${jsonError}`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const body: CreateAtcVersion = {
        what,
        how,
        actionSpec: actionSpec.value ?? {},
        testSpec: testSpec.value ?? {},
        triggerType: triggerType as CreateAtcVersion['triggerType'],
        triggerSpec: triggerSpec.value ?? {},
        hotlGate,
        determinism: determinism as CreateAtcVersion['determinism'],
        // 评审流程里人点出来的升版 = 人做的（V0）。AI 产出的版本走管线，不在这屏
        authoredBy: 'human',
      };
      const result = await api.addAtcVersion(code, body);
      setNotice(
        result.unchangedFromPrevious
          ? `已提交 v${result.versionNo}。注意：内容与上一版逐字相同。`
          : `已提交 v${result.versionNo}。`,
      );
      onSubmitted();
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="version-form">
      <p className="sub-hint">
        分三屏填，<strong>一次提交整份八段</strong>。没改的字段已按当前版
        <strong>预填</strong>并标出——提交的是完整的一版，不是补丁。
      </p>

      <div className="tabline" role="tablist" aria-label="四段填写">
        {SCREENS.map((s) => (
          <button
            key={s.key}
            type="button"
            role="tab"
            aria-selected={screen === s.key}
            onClick={() => {
              setScreen(s.key);
              setNotice(null); // 换屏就把上一屏的提交提示收掉，免得看着像这一屏的结果
            }}
          >
            {s.label}
            <span className="tab-count">卡 {s.blocks}</span>
          </button>
        ))}
      </div>

      {screen === 'what-how' && (
        <div className="sec-group">
          <section>
            <h2 className="sec">
              WHAT 这张卡产出什么 <Reused when={same.what} />
            </h2>
            <textarea rows={3} value={what} onChange={(e) => setWhat(e.target.value)} />
          </section>
          <section>
            <h2 className="sec">
              HOW 判断规则与铁律 <Reused when={same.how} />
            </h2>
            <p className="sub-hint">写「什么情况下怎么判」，不写步骤——步骤在 ACTION。</p>
            <textarea rows={5} value={how} onChange={(e) => setHow(e.target.value)} />
          </section>
        </div>
      )}

      {screen === 'action' && (
        <div className="sec-group">
          <section>
            <h2 className="sec">ACTION 动作序列</h2>
            <p className="sub-hint">
              按<strong>客户现在怎么做</strong>写，不要写成理想流程。
              常用键：<code>input_sources</code>、<code>steps</code>、<code>outputs</code>。
            </p>
            <textarea
              rows={10}
              className="mono"
              value={actionSpec.text}
              onChange={(e) => actionSpec.setText(e.target.value)}
            />
            {actionSpec.error !== null && <p className="msg">{actionSpec.error}</p>}
          </section>
          <section>
            <h2 className="sec">
              触发方式 <Reused when={same.triggerType} />
            </h2>
            <select value={triggerType} onChange={(e) => setTriggerType(e.target.value)}>
              <option value="undetermined">未定</option>
              <option value="manual">人工触发</option>
              <option value="schedule">按计划</option>
              <option value="event">按事件</option>
            </select>
            {triggerType === 'undetermined' && (
              <p className="sub-hint">
                <strong>「未定」进不了评审中。</strong>
                这一屏卡的就是它——触发方式没定下来，卡就停在建设中。
              </p>
            )}
            <textarea
              rows={4}
              className="mono"
              value={triggerSpec.text}
              onChange={(e) => triggerSpec.setText(e.target.value)}
            />
            {triggerSpec.error !== null && <p className="msg">{triggerSpec.error}</p>}
          </section>
        </div>
      )}

      {screen === 'test' && (
        <div className="sec-group">
          <section>
            <h2 className="sec">TEST 怎么算做完了</h2>
            <p className="sub-hint">
              判别法：把它交给<strong>两个互不认识的人</strong>，他们会不会得出同一个结论。
            </p>
            <textarea
              rows={8}
              className="mono"
              value={testSpec.text}
              onChange={(e) => testSpec.setText(e.target.value)}
            />
            {testSpec.error !== null && <p className="msg">{testSpec.error}</p>}
          </section>
          <section>
            <h2 className="sec">
              成熟度 <Reused when={same.determinism} />
            </h2>
            <select value={determinism} onChange={(e) => setDeterminism(e.target.value)}>
              <option value="deterministic">确定性</option>
              <option value="exploratory">探索性</option>
            </select>
            <label className="check">
              <input
                type="checkbox"
                checked={hotlGate}
                onChange={(e) => setHotlGate(e.target.checked)}
              />
              须人工审批 <Reused when={same.hotlGate} />
            </label>
            {determinism === 'exploratory' && !hotlGate && (
              <p className="msg">
                探索性的卡<strong>不开人工审批就上不了线</strong>——
                既无机器判据又无人工审批的卡不该上线。
              </p>
            )}
          </section>
        </div>
      )}

      {error !== null && <p className="msg">{error}</p>}
      {notice !== null && <p className="sub-hint">{notice}</p>}

      <div className="head-actions">
        <BusyButton busy={busy} busyLabel="提交中" type="button" onClick={() => void submit()}>
          提交这一版
        </BusyButton>
      </div>

      <p className="sub-hint">
        提交后<strong>不会自动把建设待办标成完成</strong>。
        待办由谁完成、依据哪一版，另行登记——填完表不等于做对了。
      </p>
    </div>
  );
}
